import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

/**
 * SOAP Tools — eCobra SOAP operace přes
 * `/admin/api/v1/tools/soap/execute/{operation}`. Odpověď je envelope
 * `{success, result, requestXml}` nebo `{success:false, error}`. Health check
 * přes GET `/admin/api/v1/tools/soap/health`.
 */

export interface SoapExecuteResponse {
  success: boolean;
  result?: unknown;
  /** SOAP envelope požadavku (server-side generovaný náhled). */
  requestXml?: string;
  error?: string;
  /** Pre-controller security block (401/403 bez envelope — typicky stale CSRF/session). */
  securityBlock: boolean;
  rawStatus: number;
}

/** Provede jednu SOAP operaci. Vlastní fetch (ne apiFetch) kvůli rozpoznání security bloku. */
export async function executeSoapTool(operation: string, body: Record<string, unknown>): Promise<SoapExecuteResponse> {
  const res = await fetch(`/admin/api/v1/tools/soap/execute/${operation}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  try {
    const json = JSON.parse(text) as { success: boolean; result?: unknown; requestXml?: string; error?: string };
    return { ...json, securityBlock: false, rawStatus: res.status };
  } catch {
    return {
      success: false,
      error: text || `HTTP ${res.status}`,
      securityBlock: res.status === 401 || res.status === 403,
      rawStatus: res.status,
    };
  }
}

export interface SoapHealth {
  healthy: boolean;
  endpoint?: string;
  error?: string;
}

/** Health check SOAP endpointu (jen když je SOAP konektor přítomen). */
export function useSoapHealth(enabled: boolean) {
  return useQuery({
    queryKey: ["soap-health"],
    queryFn: () => apiFetch<SoapHealth>("/admin/api/v1/tools/soap/health"),
    enabled,
    refetchOnWindowFocus: false,
  });
}
